import {
	AlertContainer,
	Button,
	DataTable,
	PageTitle,
	SearchInput,
} from '@/components';
import { useModal } from '@/hooks';
import { useDeleteBlogPost } from '@/services/mutations/blog';
import { useGetBlogPosts } from '@/services/queries/blog';
import { CircleFadingPlus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BlogPostColumns } from './BlogPostColumns';

const BlogPosts = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
	const search = searchParams.get('search') ?? undefined;

	const { data } = useGetBlogPosts({
		page,
		limit: Number(searchParams.get('limit')) || 10,
		search,
		sort: 'created_at',
		sort_direction: 'desc',
	});

	useEffect(() => {
		const params = new URLSearchParams(searchParams);
		if (!params.get('page')) params.set('page', '1');
		if (!params.get('limit')) params.set('limit', '10');
		setSearchParams(params, { replace: true });
	}, []);

	const handlePageChange = (newPage: number) => {
		const newParams = new URLSearchParams(searchParams);
		newParams.set('page', String(newPage));
		setSearchParams(newParams, { replace: true });
		setPage(newPage);
	};

	const {
		setConfirmText,
		setCancelText,
		openModal,
		setOnConfirm,
		closeModal,
	} = useModal();
	const { mutate: deleteMutate } = useDeleteBlogPost(closeModal);

	const handleDelete = useCallback(
		(post: Blog.BlogPost) => {
			setConfirmText('Confirmar');
			setCancelText('Cancelar');

			const message = (
				<p className="text-base text-dark">
					Tem certeza que deseja deletar o post{' '}
					<span className="font-semibold text-red-500">
						"{post.title}"
					</span>
					?
				</p>
			);

			openModal({ content: <AlertContainer message={message} /> });
			setOnConfirm(() => deleteMutate(post.id));
		},
		[setConfirmText, setCancelText, openModal, setOnConfirm, deleteMutate],
	);

	const handleEdit = useCallback(
		(post: Blog.BlogPost) => {
			navigate(`/admin/blog/editar/${post.id}`);
		},
		[navigate],
	);

	const handlePreview = useCallback(
		(post: Blog.BlogPost) => {
			navigate(`/admin/blog/editar/${post.id}?tab=preview`);
		},
		[navigate],
	);

	const handleCopyLink = useCallback(async (post: Blog.BlogPost) => {
		const url = `${
			import.meta.env.VITE_FRONTEND_URL
		}/blog/${encodeURIComponent(post.slug)}`;
		try {
			await navigator.clipboard.writeText(url);
			toast.success('Link copiado para a área de transferência!');
		} catch {
			toast.error('Não foi possível copiar o link.');
		}
	}, []);

	const columns = useMemo(
		() =>
			BlogPostColumns({
				onEdit: handleEdit,
				onDelete: handleDelete,
				onPreview: handlePreview,
				onCopyLink: handleCopyLink,
			}),
		[handleEdit, handleDelete, handlePreview, handleCopyLink],
	);

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="flex items-center justify-between">
				<div className="space-y-1">
					<PageTitle text="Blog" />
					<p className="text-sm text-stone-400">
						Gerencie os posts publicados no blog.
					</p>
				</div>
				<Button
					className="rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800"
					startIcon={<CircleFadingPlus className="w-4 text-light" />}
					text="Novo post"
					onClick={() => navigate('criar')}
				/>
			</div>

			<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="w-full sm:max-w-md">
					<SearchInput
						placeholder="Pesquisar por título..."
						className="bg-inherit"
					/>
				</div>
			</div>

			{data && (
				<DataTable
					columns={columns}
					data={data.data}
					page={page}
					limit={10}
					totalItems={data.pagination.total}
					onPageChange={handlePageChange}
				/>
			)}
		</section>
	);
};

export { BlogPosts };
